// src/components/ia-dossier/IADossierModal.tsx
//
// Modale de capture lead pour accéder au dossier réservé
// /dossier-ia-recrutement. Workflow :
//   1. Visiteur clique sur CTA depuis /entreprise/ia
//   2. Modale s'ouvre, 4 champs (prénom, nom, email pro, société)
//   3. Submit → insert Supabase leads (source = ia_dossier_recrutement)
//      + notification email à gerald via whitepaper-notify Edge Function
//      + set localStorage flag DOSSIER_IA_STORAGE_KEY = '1'
//   4. Redirige vers /dossier-ia-recrutement

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Brain,
  ArrowRight,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { DOSSIER_IA_STORAGE_KEY } from '../../pages/dossier-ia-recrutement';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}

export default function IADossierModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Esc pour fermer
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset state à la fermeture (avec délai d'animation)
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setForm({ firstName: '', lastName: '', email: '', company: '' });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  function setField<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const { firstName, lastName, email, company } = form;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !company.trim()) {
      setError('Tous les champs sont requis pour accéder au dossier.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Format email invalide — utilisez votre email professionnel.');
      return;
    }

    setSubmitting(true);

    const cleanFirst = firstName.trim();
    const cleanLast = lastName.trim();
    const cleanEmail = email.trim().toLowerCase();
    const cleanCompany = company.trim();

    try {
      // 1) Insert lead Supabase
      const { error: insertError } = await supabase.from('leads').insert([
        {
          first_name: cleanFirst,
          last_name: cleanLast,
          email: cleanEmail,
          company: cleanCompany,
          source: 'ia_dossier_recrutement',
        },
      ]);
      if (insertError) {
        console.error('[ia-dossier] insert error:', insertError.message);
        setError(
          "Impossible d'enregistrer votre demande. Réessayez ou contactez contact@recruteurs.io.",
        );
        return;
      }

      // 2) Notification email à gerald (réutilise whitepaper-notify, on
      // adapte le payload pour qu'il fonctionne avec le format attendu)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
      if (supabaseUrl && anon) {
        fetch(
          `${supabaseUrl.replace(/\/$/, '')}/functions/v1/whitepaper-notify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${anon}`,
              apikey: anon,
            },
            body: JSON.stringify({
              paper: {
                slug: 'ia-dossier-recrutement',
                title: 'Dossier IA & Recrutement — accès demandé',
              },
              lead: {
                firstName: cleanFirst,
                lastName: cleanLast,
                email: cleanEmail,
                company: cleanCompany,
              },
              metadata: {
                submittedAt: new Date().toISOString(),
                referrer:
                  typeof window !== 'undefined' ? window.location.pathname : '',
                userAgent:
                  typeof navigator !== 'undefined' ? navigator.userAgent : '',
              },
            }),
            keepalive: true,
          },
        ).catch((err) =>
          console.warn('[ia-dossier] notify failed:', err),
        );
      }

      // 3) Set le flag localStorage pour débloquer la page
      try {
        localStorage.setItem(DOSSIER_IA_STORAGE_KEY, '1');
      } catch {
        // localStorage indispo — on continue quand même
      }

      // 4) Affiche success state pendant 1.5s puis redirige
      setSubmitted(true);
      setTimeout(() => {
        navigate('/dossier-ia-recrutement');
      }, 1500);
    } catch (err) {
      console.error('[ia-dossier] submit error:', err);
      setError('Une erreur est survenue. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ia-dossier-modal-title"
        >
          <div
            onClick={onClose}
            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
            aria-hidden
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-[1.75rem] bg-white shadow-2xl ring-1 ring-neutral-200"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 ring-1 ring-neutral-200 hover:bg-white hover:ring-neutral-300 flex items-center justify-center text-neutral-500 hover:text-[#30475e] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-8 md:p-10">
              {!submitted && (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center shadow-md shadow-[#d03484]/20">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#d03484]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#d03484]">
                      <Sparkles className="w-3 h-3" />
                      Accès au dossier privé
                    </span>
                  </div>

                  <h2
                    id="ia-dossier-modal-title"
                    className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] mb-3 leading-[1.15]"
                  >
                    L'IA dans le recrutement —{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                      apports, limites, sources.
                    </span>
                  </h2>
                  <p className="text-sm md:text-base text-neutral-600 leading-relaxed mb-6">
                    Dossier exclusif réservé aux dirigeants RH et recruteurs.
                    Quatre apports concrets de l'IA (sourcing, sélection,
                    évaluation, automatisation), trois limites structurelles,
                    et toutes les sources citées vérifiables.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#30475e] uppercase tracking-wider mb-1.5">
                          Prénom <span className="text-[#d03484]">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.firstName}
                          onChange={(e) => setField('firstName', e.target.value)}
                          autoComplete="given-name"
                          disabled={submitting}
                          className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#30475e] uppercase tracking-wider mb-1.5">
                          Nom <span className="text-[#d03484]">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.lastName}
                          onChange={(e) => setField('lastName', e.target.value)}
                          autoComplete="family-name"
                          disabled={submitting}
                          className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#30475e] uppercase tracking-wider mb-1.5">
                        Email professionnel <span className="text-[#d03484]">*</span>
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setField('email', e.target.value)}
                        autoComplete="email"
                        disabled={submitting}
                        placeholder="prenom.nom@entreprise.com"
                        className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] placeholder:text-neutral-400 outline-none transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#30475e] uppercase tracking-wider mb-1.5">
                        Nom de société <span className="text-[#d03484]">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => setField('company', e.target.value)}
                        autoComplete="organization"
                        disabled={submitting}
                        className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                      />
                    </div>

                    {error && (
                      <div className="rounded-xl bg-red-50 ring-1 ring-red-200 px-4 py-2.5 text-sm text-red-700 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-3 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0 whitespace-nowrap"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Validation en cours...
                        </>
                      ) : (
                        <>
                          Accéder au dossier
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>

                    <p className="text-[11px] text-neutral-500 text-center leading-relaxed">
                      Vos données restent en France (Supabase EU). Pas de spam,
                      désabonnement en 1 clic. RGPD compliant.
                    </p>
                  </form>
                </>
              )}

              {submitted && (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] text-white shadow-lg shadow-[#d03484]/20 mb-5">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] mb-3 leading-tight">
                    Merci {form.firstName} !{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                      Accès débloqué.
                    </span>
                  </h2>
                  <p className="text-sm md:text-base text-neutral-600 leading-relaxed mb-5">
                    Redirection vers le dossier dans un instant…
                  </p>
                  <Loader2 className="w-5 h-5 animate-spin text-[#d03484] mx-auto" />
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
